/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
import _ from 'lodash'
import { useStaticQuery, graphql } from 'gatsby'
import { useMemo } from 'react'
import {
  useOrderMethod,
  useUser,
} from '../../vendor/react-omnitech-api'
import { useThemeConfig } from '../use-theme-config'
import env from '../../config/env'

// PUBLIC METHODS
function getOrderMethodUpdateCartAction({
  orderMethod = {},
  cartShipmentId,
  deliveryAddressId,
  pickupStoreId,
}) {
  const {
    deliveryType,
    deliveryTypeForWarehouse,
  } = orderMethod
  const selectedDeliveryType = (pickupStoreId === 'warehouse' && deliveryTypeForWarehouse) || deliveryType
  const updateCartAction = _.omitBy(
    {
      actionType: 'update_cart_shipment',
      id: cartShipmentId,
      deliveryType: selectedDeliveryType,
      deliveryAddressId,
      pickupStoreId,
    },
    (value) => (
      _.isNil(value)
      || value === '' // prevent empty string
    ),
  )
  if (_.isEmpty(_.omit(updateCartAction, ['actionType', 'id']))) return {}
  return updateCartAction
}

function resolveConfig(value, defaultValue) {
  if (_.isString(value)) {
    return {
      ...defaultValue,
      [value]: true,
    }
  }
  if (_.isArray(value)) {
    return {
      ...defaultValue,
      ..._.reduce(value, (result, key) => {
        result = {
          ...result,
          [key]: true,
        }
        return result
      }, {}),
    }
  }
  if (_.isPlainObject(value)) {
    return {
      ...defaultValue,
      ...value,
    }
  }
  return defaultValue
}

function resolveEnableStores({ enableStores, enableStoresForTester, isTester }) {
  if (isTester) {
    return _.isNil(enableStoresForTester) ? enableStores : enableStoresForTester
  }
  return enableStores
}

function resolveSelectStoreApproach(value) {
  const defaultValue = {
    user: false, // Store select by user manually
    deliveryAddress: false, // Store select by delivery address lat long, e.g. zeek
    openTableUrl: false, // fnb, Open table by OT url
    checkout: false, // General retail approach
    // It will force delivery_type to shipped_form_warehouse,
    //   if user select warehouse as delivery store
    warehouse: false,
    none: false, // No pickup store required
  }

  let result = resolveConfig(value, defaultValue)

  if (!_.some(_.values(result))) {
    result = {
      ...result,
      none: true,
    }
  }

  return resolveConfig(value, defaultValue)
}

function resolveUiPosition(value) {
  const defaultValue = {
    'panel|dropdown': true, // Use dropdown to select order method if uiPosition is not set
    'panel|button': false,
  }

  return resolveConfig(value, defaultValue)
}

function resolveDisplay(value) {
  const defaultValue = {
    storeName: true,
    dineInTableNumber: true,
    dineInTableSubNumber: true,
  }

  return resolveConfig(value, defaultValue)
}

function useOrderMethodHook() {
  const apiOrderMethod = useOrderMethod()
  const { user } = useUser()
  const {
    getConfig,
  } = useThemeConfig()
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            version
          }
        }
      }
    `,
  )
  const isTester = _.includes(_.get(user, 'roles', []), 'tester')

  const resolveOrderMethod = (om) => {
    const {
      code,
      enableStores,
      enableStoresForTester,
      deliveryTypeForWarehouse = 'shipped_form_warehouse',
      selectStoreBy,
      selectStoreApproach,
      uiPosition,
      display,
      disabled = false,
    } = om
    return {
      ...om,
      enableStores: resolveEnableStores({ enableStores, enableStoresForTester, isTester }),
      selectStoreApproach: resolveSelectStoreApproach(selectStoreBy || selectStoreApproach),
      uiPosition: resolveUiPosition(uiPosition),
      display: resolveDisplay(display),
      deliveryTypeForWarehouse,
      initPage: _.get(om, 'initPage', code === 'dineIn' ? 'categories' : undefined),
      disabled,
    }
  }

  const availableOrderMethod = useMemo(() => (
    _.filter(_.map(
      getConfig('config.availableOrderMethod', []),
      resolveOrderMethod,
    ), ['disabled', false])
  ), [getConfig])

  const selectableOrderMethod = useMemo(() => (
    _.filter(
      availableOrderMethod,
      ({ uiPosition }) => (
        _.get(uiPosition, 'panel|dropdown', false)
      ),
    )
  ), [availableOrderMethod])

  const resolvedOrderMethod = useMemo(() => {
    const {
      orderMethod = {},
      setOrderMethod,
      ...rest
    } = apiOrderMethod
    const selectedOrderMethodCode = _.get(orderMethod, 'code')
    const fallbackOrderMethod = {
      ...resolveOrderMethod(orderMethod),
      commerceChannel: _.get(env, 'config.api.commerceChannel'),
    }
    const selectedOrderMethod = _.find(
      availableOrderMethod,
      { code: selectedOrderMethodCode },
    ) || fallbackOrderMethod
    const handleSetOrderMethod = (newOrderMethod = {}) => {
      setOrderMethod({
        ...newOrderMethod,
        schemaVersion: _.get(site, 'siteMetadata.version'),
      })
    }
    return {
      orderMethod: selectedOrderMethod,
      setOrderMethod: handleSetOrderMethod,
      ...rest,
    }
  }, [apiOrderMethod])

  return {
    availableOrderMethod,
    getOrderMethodUpdateCartAction,
    selectableOrderMethod,
    ...resolvedOrderMethod,
  };
}

export default useOrderMethodHook
